import React, { Dispatch, SetStateAction, useEffect } from "react";
import Link from "next/link";
import Hamburger from "hamburger-react";
import { useRouter } from "next/router";

import Icon from "../common/Icon";
import CircleBadge from "../common/CircleBadge";
import Button from "../common/Button";
import LogoLink from "./LogoLink";

import { useAppSelector } from "../../utils/hooks/store";
import {
  CAPABILITIES,
  COMPONENTS,
  DOCS,
  MY_ACCOUNT,
  SHOPPING_CART,
  UPLOAD_BOARD,
} from "../../constants/routes";
import { useShoppingCartSummary } from "../../utils/hooks/commonly-stored/shoppingCart";
import * as styledElements from "./HeaderStyles";
import SearchBar from "../component-search/SearchBar";
import {
  COLORS,
  HideIfAboveWidth,
  ShowIfAboveWidth,
} from "../../constants/Styles";
import { HAMBURGER_PX, HEADER_HEIGHT, SHRINK_SEARCH_PX } from "./HeaderStyles";
import Drawer from "react-modern-drawer";
import NotificationCount from "../common/NotificationCount";

interface HeaderLink {
  imageFileName: string;
  href: string;
  name: string;
  alt: string;
}

const links: HeaderLink[] = [
  {
    imageFileName: "parts",
    href: COMPONENTS,
    name: "Parts",
    alt: "search-parts",
  },
  {
    imageFileName: "boards_gray",
    href: CAPABILITIES,
    name: "Capabilities",
    alt: "capabilities",
  },
  {
    imageFileName: "docs",
    href: DOCS,
    name: "Docs",
    alt: "docs",
  },
];

const getMobileLinks = (signedIn: boolean): HeaderLink[] => {
  return [
    ...links,
    ...(signedIn
      ? [
          {
            imageFileName: "shopping_cart",
            href: SHOPPING_CART,
            name: "Shopping cart",
            alt: "shopping cart",
          },
          {
            imageFileName: "account",
            href: MY_ACCOUNT,
            name: "My account",
            alt: "my account",
          },
        ]
      : [
          {
            imageFileName: "account",
            href: MY_ACCOUNT,
            name: "Log in/register",
            alt: "Log in or register",
          },
        ]),
    {
      imageFileName: "wrench_gray",
      href: UPLOAD_BOARD,
      name: "Order board",
      alt: "order board",
    },
  ];
};

export interface HeaderProps {
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
  isMenuOpen: boolean;
}

export const HeaderHamburger = ({ setMenuOpen, isMenuOpen }: HeaderProps) => {
  const router = useRouter();
  const signedIn = useAppSelector((state) => state.auth.signedIn);

  useEffect(() => {
    setMenuOpen(false);
  }, [setMenuOpen, router.pathname]);

  const clickHamburgerLink = (link: HeaderLink) => {
    if (router.pathname == link.href) {
      setMenuOpen(false);
    } else {
      void router.push(link.href);
    }
  };

  return (
    <styledElements.HamburgerContainer>
      <Hamburger
        direction="right"
        label="Show page links"
        toggled={isMenuOpen}
        toggle={setMenuOpen}
        onToggle={() => {
          document.body.scrollTop = document.documentElement.scrollTop = 0;
        }}
      />
      <Drawer
        open={isMenuOpen}
        onClose={() => setMenuOpen(false)}
        direction="right"
        enableOverlay
        overlayOpacity={0}
        style={{
          width: "100vw",
          maxWidth: "30rem",
          height: `calc(100vh - ${HEADER_HEIGHT})`,
          overflowY: "scroll",
          top: HEADER_HEIGHT,
          backgroundColor: COLORS.background,
        }}
        lockBackgroundScroll={true}
      >
        <styledElements.LinksContainer>
          {getMobileLinks(signedIn).map((link, linkIndex) => (
            <styledElements.LinkContainer
              key={`link-${link.href}-${linkIndex}`}
            >
              <styledElements.MobileLinkComponent
                onClick={() => clickHamburgerLink(link)}
              >
                <Icon src={`/icons/${link.imageFileName}.svg`} alt={link.alt} />
                {link.name}
              </styledElements.MobileLinkComponent>
            </styledElements.LinkContainer>
          ))}
        </styledElements.LinksContainer>
      </Drawer>
    </styledElements.HamburgerContainer>
  );
};

const Header = ({ setMenuOpen, isMenuOpen }: HeaderProps) => {
  const router = useRouter();
  const { shoppingCartSummary } = useShoppingCartSummary();
  const signedIn = useAppSelector((state) => state.auth.signedIn);
  const logoLink = <LogoLink onClick={() => setMenuOpen(false)} />;
  const myAccountPage = router.pathname == MY_ACCOUNT;
  const shoppingCartPage = router.pathname == SHOPPING_CART;

  return (
    <>
      <HideIfAboveWidth width={HAMBURGER_PX}>
        <styledElements.Header>
          <styledElements.HeaderRow>
            {logoLink}
            <HeaderHamburger
              isMenuOpen={isMenuOpen}
              setMenuOpen={setMenuOpen}
            />
          </styledElements.HeaderRow>
        </styledElements.Header>
      </HideIfAboveWidth>
      <ShowIfAboveWidth width={HAMBURGER_PX}>
        <styledElements.Header>
          <styledElements.HeaderRow>
            {logoLink}
            {links.map((link) => (
              <styledElements.Navigation key={link.href}>
                <styledElements.LinkComponent href={link.href} passHref>
                  <>
                    <Icon
                      src={`/icons/${link.imageFileName}.svg`}
                      alt={link.alt}
                      styleProps={{ marginRight: ".25rem" }}
                    />
                    {link.name}
                  </>
                </styledElements.LinkComponent>
              </styledElements.Navigation>
            ))}
            <styledElements.RightSection>
              {router.pathname != COMPONENTS && (
                <>
                  <ShowIfAboveWidth width={SHRINK_SEARCH_PX}>
                    <SearchBar updatePath />
                  </ShowIfAboveWidth>
                  <HideIfAboveWidth width={SHRINK_SEARCH_PX}>
                    <CircleBadge
                      iconProps={{
                        src: "/icons/search.svg",
                        alt: "search",
                      }}
                      onClick={() => void router.push(COMPONENTS)}
                    />
                  </HideIfAboveWidth>
                </>
              )}
              {signedIn && (
                <>
                  <styledElements.CircleLinkContainer
                    isSelected={shoppingCartPage}
                  >
                    <Link href={SHOPPING_CART} passHref>
                      <CircleBadge
                        iconProps={{
                          src: `/icons/shopping_cart${
                            shoppingCartPage ? "_white" : ""
                          }.svg`,
                          alt: "shopping cart",
                        }}
                      />
                    </Link>
                    <NotificationCount
                      numNotifications={shoppingCartSummary?.num_items}
                    />
                  </styledElements.CircleLinkContainer>
                  <styledElements.CircleLinkContainer
                    isSelected={myAccountPage}
                  >
                    <Link href={MY_ACCOUNT} passHref>
                      <CircleBadge
                        iconProps={{
                          src: `/icons/account${
                            myAccountPage ? "_white" : ""
                          }.svg`,
                          alt: "my account",
                        }}
                      />
                    </Link>
                  </styledElements.CircleLinkContainer>
                </>
              )}
              <Button
                label={signedIn ? "Order board" : "Log in/register"}
                leftIconProps={
                  signedIn
                    ? {
                        src: "/icons/wrench.svg",
                        alt: "order board",
                        iconDimension: 24,
                      }
                    : undefined
                }
                styleProps={signedIn ? {} : { marginLeft: "0.5rem" }}
                onClick={() =>
                  void router.push(signedIn ? UPLOAD_BOARD : MY_ACCOUNT)
                }
              />
            </styledElements.RightSection>
          </styledElements.HeaderRow>
        </styledElements.Header>
      </ShowIfAboveWidth>
    </>
  );
};

export default Header;
